/**
 * Affiliate referral program page (fs809) — operator / publisher view.
 *
 * Composition:
 *   - Header (emerald → sky gradient).
 *   - Summary tile row: total earned, total pending, referrals recruited,
 *     program bonus %.
 *   - "Your referral link" card with code + copyable full link + rotate button.
 *   - Referred publishers table (name/id, joined date, status, earnings).
 *   - Program settings card: bonus percent + payout currency (static view).
 *
 * Brand: emerald→sky headline gradient, zinc borders, 6–8px rounding, light
 * theme only, no emojis.
 *
 * All data plumbing lives in `./api` + `./hooks`; this file is render + state
 * wiring only (no raw fetch).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	buildReferralLink,
	DEFAULT_PROGRAM_SETTINGS,
	formatDate,
	formatInteger,
	formatMoney,
	formatStatus,
	referralEarningsText,
	totalEarnedText,
	totalPendingText,
	type ReferralRow,
	type ReferrerSummary,
} from "./api";
import {
	useMintCode,
	useReferralCode,
	useReferrals,
	useReferrerSummary,
	useRotateCode,
} from "./hooks";

// ---------------------------------------------------------------------------
// Style tokens — aligned with dash brand rules.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BADGE_EMERALD =
	"inline-flex items-center rounded-[6px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700";
const BADGE_SKY =
	"inline-flex items-center rounded-[6px] border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700";
const BADGE_ZINC =
	"inline-flex items-center rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-semibold text-zinc-700";

function statusBadge(s: ReferralRow["payout_status"]): string {
	if (s === "paid") return BADGE_EMERALD;
	if (s === "pending") return BADGE_SKY;
	return BADGE_ZINC;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const DEFAULT_AFFILIATE_ID = "me";

export function AffiliateReferralPage() {
	const [affiliateId, setAffiliateId] = useState<string>(DEFAULT_AFFILIATE_ID);
	const [copied, setCopied] = useState<boolean>(false);

	const codeQuery = useReferralCode(affiliateId);
	const referralsQuery = useReferrals(affiliateId);
	const summaryQuery = useReferrerSummary(affiliateId);

	const mintMut = useMintCode();
	const rotateMut = useRotateCode();

	const busy = mintMut.isPending || rotateMut.isPending;

	const code = codeQuery.data?.code ?? null;
	const referralLink = useMemo(() => {
		if (!code) return "";
		return buildReferralLink(code);
	}, [code]);

	const handleMint = useCallback(() => {
		if (!affiliateId.trim()) return;
		mintMut.mutate(affiliateId.trim());
	}, [affiliateId, mintMut]);

	const handleRotate = useCallback(() => {
		if (!affiliateId.trim()) return;
		rotateMut.mutate(affiliateId.trim());
	}, [affiliateId, rotateMut]);

	const handleCopy = useCallback(async () => {
		if (!referralLink) return;
		try {
			const nav = (globalThis as { navigator?: Navigator }).navigator;
			if (nav?.clipboard?.writeText) {
				await nav.clipboard.writeText(referralLink);
			}
			setCopied(true);
		} catch {
			setCopied(true);
		}
	}, [referralLink]);

	useEffect(() => {
		if (!copied) return;
		const t = setTimeout(() => setCopied(false), 1500);
		return () => clearTimeout(t);
	}, [copied]);

	const summary = summaryQuery.data ?? null;
	const referrals = referralsQuery.data ?? [];

	return (
		<main
			data-testid="affiliate-referral-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Header />

			<SummaryTiles
				summary={summary}
				referralsCount={referrals.length}
				loading={summaryQuery.isLoading || referralsQuery.isLoading}
			/>

			<AffiliateLookupCard
				affiliateId={affiliateId}
				setAffiliateId={setAffiliateId}
			/>

			<ReferralLinkCard
				code={code}
				link={referralLink}
				copied={copied}
				onCopy={handleCopy}
				onMint={handleMint}
				onRotate={handleRotate}
				busy={busy}
				loading={codeQuery.isLoading}
				error={
					codeQuery.error?.message ??
					mintMut.error?.message ??
					rotateMut.error?.message ??
					null
				}
			/>

			<ReferredPublishersCard
				rows={referrals}
				loading={referralsQuery.isLoading}
				error={referralsQuery.error?.message ?? null}
			/>

			<ProgramSettingsCard />
		</main>
	);
}

export default AffiliateReferralPage;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Affiliates
			</p>
			<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold tracking-tight text-transparent sm:text-4xl">
				Your referral program
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Invite other publishers with your personal link. When they sign up and
				start earning, you get a share of every qualifying payout — tracked
				here in real time.
			</p>
		</header>
	);
}

function SummaryTiles(props: {
	summary: ReferrerSummary | null;
	referralsCount: number;
	loading: boolean;
}) {
	const cells = [
		{
			label: "Total earned",
			value: totalEarnedText(props.summary),
			testid: "tile-total-earned",
		},
		{
			label: "Pending",
			value: totalPendingText(props.summary),
			testid: "tile-total-pending",
		},
		{
			label: "Publishers referred",
			value: formatInteger(props.referralsCount),
			testid: "tile-referrals-count",
		},
		{
			label: "Bonus rate",
			value: `${DEFAULT_PROGRAM_SETTINGS.bonus_percent}%`,
			testid: "tile-bonus-rate",
		},
	];
	return (
		<section
			aria-label="Referral program summary"
			className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
			data-testid="summary-tiles"
		>
			{cells.map((c) => (
				<div
					key={c.label}
					className={`${CARD} flex flex-col gap-1 p-4`}
					data-testid={c.testid}
				>
					<p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						{c.label}
					</p>
					<p className="text-2xl font-semibold tabular-nums text-zinc-900">
						{props.loading ? "—" : c.value}
					</p>
				</div>
			))}
		</section>
	);
}

function AffiliateLookupCard(props: {
	affiliateId: string;
	setAffiliateId: (v: string) => void;
}) {
	return (
		<section
			className={`${CARD} flex flex-col gap-3 p-5 sm:flex-row sm:items-end sm:justify-between`}
			data-testid="affiliate-lookup-card"
		>
			<div className="flex flex-1 flex-col gap-1">
				<label className={LABEL} htmlFor="afr-affiliate">
					Affiliate id
				</label>
				<input
					id="afr-affiliate"
					type="text"
					className={INPUT}
					placeholder="me"
					value={props.affiliateId}
					onChange={(e) => props.setAffiliateId(e.currentTarget.value)}
					data-testid="affiliate-id-input"
				/>
				<p className="text-xs text-zinc-500">
					Enter the affiliate id you want to view. Defaults to your own.
				</p>
			</div>
		</section>
	);
}

function ReferralLinkCard(props: {
	code: string | null;
	link: string;
	copied: boolean;
	onCopy: () => void;
	onMint: () => void;
	onRotate: () => void;
	busy: boolean;
	loading: boolean;
	error: string | null;
}) {
	return (
		<section className={`${CARD} flex flex-col`} data-testid="referral-link-card">
			<header className="flex flex-col gap-3 border-b border-zinc-200 p-5 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h2 className="text-base font-semibold text-zinc-900">
						Your referral link
					</h2>
					<p className="text-sm text-zinc-500">
						Share this link with publishers you want to refer. Rotating the
						code invalidates the previous link.
					</p>
				</div>
				<div className="flex items-center gap-2">
					{props.code ? (
						<button
							type="button"
							className={BTN_NEUTRAL}
							onClick={props.onRotate}
							disabled={props.busy}
							data-testid="rotate-code-btn"
						>
							Rotate code
						</button>
					) : (
						<button
							type="button"
							className={BTN_PRIMARY}
							onClick={props.onMint}
							disabled={props.busy}
							data-testid="mint-code-btn"
						>
							Generate link
						</button>
					)}
				</div>
			</header>

			{props.error ? (
				<div className="p-5 text-sm text-red-600" role="alert">
					{props.error}
				</div>
			) : props.loading ? (
				<div className="p-5 text-sm text-zinc-500">Loading referral link.</div>
			) : !props.code ? (
				<div className="p-5 text-sm text-zinc-500">
					No referral code yet. Click "Generate link" to mint one.
				</div>
			) : (
				<div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center">
					<div className="flex-1">
						<p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
							Code
						</p>
						<p
							className="mt-1 font-mono text-sm text-zinc-900"
							data-testid="referral-code-value"
						>
							{props.code}
						</p>
					</div>
					<div className="flex flex-1 flex-col gap-1">
						<label className={LABEL} htmlFor="afr-link">
							Full link
						</label>
						<div className="flex items-stretch gap-2">
							<input
								id="afr-link"
								type="text"
								readOnly
								value={props.link}
								className={`${INPUT} flex-1 font-mono text-xs`}
								data-testid="referral-link-input"
							/>
							<button
								type="button"
								className={BTN_PRIMARY}
								onClick={props.onCopy}
								disabled={!props.link}
								data-testid="copy-link-btn"
							>
								{props.copied ? "Copied" : "Copy"}
							</button>
						</div>
					</div>
				</div>
			)}
		</section>
	);
}

function ReferredPublishersCard(props: {
	rows: readonly ReferralRow[];
	loading: boolean;
	error: string | null;
}) {
	return (
		<section
			className={`${CARD} flex flex-col`}
			data-testid="referred-publishers-card"
		>
			<header className="border-b border-zinc-200 p-5">
				<h2 className="text-base font-semibold text-zinc-900">
					Referred publishers
				</h2>
				<p className="text-sm text-zinc-500">
					Everyone who signed up through your link, plus their current bonus
					status.
				</p>
			</header>

			{props.error ? (
				<div className="p-5 text-sm text-red-600" role="alert">
					{props.error}
				</div>
			) : props.loading ? (
				<div
					className="p-5 text-sm text-zinc-500"
					data-testid="referrals-loading"
				>
					Loading referrals.
				</div>
			) : props.rows.length === 0 ? (
				<div
					className="p-5 text-sm text-zinc-500"
					data-testid="referrals-empty"
				>
					No referrals yet. Share your link above to get started.
				</div>
			) : (
				<div className="overflow-x-auto">
					<table
						className="w-full text-left text-sm"
						data-testid="referrals-table"
					>
						<thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
							<tr>
								<th className="px-4 py-2">Publisher</th>
								<th className="px-4 py-2">Joined</th>
								<th className="px-4 py-2">Status</th>
								<th className="px-4 py-2 text-right">Earnings</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{props.rows.map((row) => (
								<tr key={row.id} data-testid={`referral-row-${row.id}`}>
									<td className="px-4 py-2 text-zinc-900 tabular-nums">
										{row.referee_id}
									</td>
									<td className="px-4 py-2 text-zinc-500">
										{formatDate(row.created_at)}
									</td>
									<td className="px-4 py-2">
										<span
											className={statusBadge(row.payout_status)}
											data-testid={`referral-status-${row.id}`}
										>
											{formatStatus(row.payout_status)}
										</span>
									</td>
									<td className="px-4 py-2 text-right text-zinc-900 tabular-nums">
										{referralEarningsText(row)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

function ProgramSettingsCard() {
	const settings = DEFAULT_PROGRAM_SETTINGS;
	return (
		<section
			className={`${CARD} flex flex-col`}
			data-testid="program-settings-card"
		>
			<header className="border-b border-zinc-200 p-5">
				<h2 className="text-base font-semibold text-zinc-900">
					Program settings
				</h2>
				<p className="text-sm text-zinc-500">
					These values apply to every new referral. Contact your account
					manager to change them.
				</p>
			</header>
			<div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
				<div className="flex flex-col gap-1">
					<p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						Bonus percentage
					</p>
					<p
						className="text-xl font-semibold tabular-nums text-zinc-900"
						data-testid="settings-bonus-percent"
					>
						{settings.bonus_percent}%
					</p>
					<p className="text-xs text-zinc-500">
						Of each referred publisher's qualifying payout.
					</p>
				</div>
				<div className="flex flex-col gap-1">
					<p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						Payout currency
					</p>
					<p
						className="text-xl font-semibold tabular-nums text-zinc-900"
						data-testid="settings-currency"
					>
						{settings.currency}
					</p>
					<p className="text-xs text-zinc-500">
						Example bonus on $100 payout:{" "}
						<span className="font-mono text-zinc-700">
							{formatMoney(
								(100 * 100 * settings.bonus_percent) / 100,
								settings.currency,
							)}
						</span>
					</p>
				</div>
			</div>
		</section>
	);
}
